import { useState, useRef, useEffect } from "react";
import type { LucideIcon } from "lucide-react";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
  GripVertical,
  Eye,
  EyeOff,
  Trash2,
  ChevronUp,
  ChevronDown,
  MoreVertical,
  Copy,
} from "lucide-react";

export interface SortableSectionItemProps {
  id: string;
  label: string;
  icon: LucideIcon;
  visible: boolean;
  isActive: boolean;
  onSelect: () => void;
  onToggleVisibility: () => void;
  onDelete?: () => void;
  onDuplicate?: () => void;
  itemCount?: number | null;
  onMoveUp?: () => void;
  onMoveDown?: () => void;
  isFirst?: boolean;
  isLast?: boolean;
  disabled?: boolean;
  isPinned?: boolean;
}

export function SortableSectionItem({
  id,
  label,
  icon: Icon,
  visible,
  isActive,
  onSelect,
  onToggleVisibility,
  onDelete,
  onDuplicate,
  itemCount,
  onMoveUp,
  onMoveDown,
  isFirst,
  isLast,
  disabled,
  isPinned = true,
}: SortableSectionItemProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id, disabled });

  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const menuTriggerRef = useRef<HTMLButtonElement>(null);

  // Close menu on outside click or Escape
  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (
        menuRef.current &&
        !menuRef.current.contains(e.target as Node) &&
        menuTriggerRef.current &&
        !menuTriggerRef.current.contains(e.target as Node)
      ) {
        setIsMenuOpen(false);
      }
    }

    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === "Escape" && isMenuOpen) {
        setIsMenuOpen(false);
        menuTriggerRef.current?.focus();
      }
    }

    if (isMenuOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      document.addEventListener("keydown", handleKeyDown);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [isMenuOpen]);

  const style = {
    transform: CSS.Translate.toString(transform),
    transition: transition || "transform 200ms cubic-bezier(0.2, 0.8, 0.2, 1)",
    zIndex: isDragging ? 50 : undefined,
    opacity: isDragging ? 0.3 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`group relative flex items-center justify-start rounded-xl border transition-all ${
        isActive
          ? "bg-card border-primary shadow-xs ring-1 ring-primary/30"
          : "border-border/70 bg-card/60 hover:bg-card hover:border-border"
      } ${!visible ? "opacity-60" : ""}`}
    >
      {/* ── 1. Drag Handle Button (WCAG Compliant - Not Nested) ── */}
      {disabled ? (
        <div className={`shrink-0 transition-all duration-200 ${!isPinned ? 'w-0 opacity-0 group-hover/sidebar:w-8 group-hover/sidebar:opacity-100 overflow-hidden' : 'w-8'}`} />
      ) : (
        <button
          type="button"
          {...attributes}
          {...listeners}
          aria-label={`Drag to reorder ${label} section`}
          className={`h-11 flex items-center justify-center cursor-grab active:cursor-grabbing text-muted-foreground/50 hover:text-foreground shrink-0 outline-none rounded-l-xl focus-visible:ring-2 focus-visible:ring-primary transition-all duration-200 ${!isPinned ? 'w-0 opacity-0 overflow-hidden group-hover/sidebar:w-8 group-hover/sidebar:opacity-100' : 'w-8'}`}
        >
          <GripVertical className="h-4 w-4" />
        </button>
      )}

      {/* ── 2. Primary Section Selection Button ── */}
      <button
        type="button"
        onClick={onSelect}
        aria-selected={isActive}
        aria-label={`${label} section${itemCount !== undefined && itemCount !== null ? `, ${itemCount} items` : ""}${!visible ? ", hidden" : ""}`}
        title={!isPinned ? `${label}${!visible ? " (Hidden)" : ""}` : undefined}
        className={`flex-1 flex items-center py-2 min-w-0 text-left outline-none cursor-pointer focus-visible:ring-2 focus-visible:ring-primary rounded-lg transition-all ${
          !isPinned
            ? "justify-start px-0 group-hover/sidebar:px-1 gap-0 group-hover/sidebar:gap-2.5"
            : "justify-start px-1 gap-2.5"
        }`}
      >
        {/* Icon wrapper to ensure perfect rail centering when collapsed */}
        <div className={`flex items-center justify-center shrink-0 ${!isPinned ? "w-10 h-7 group-hover/sidebar:w-7" : "w-7 h-7"}`}>
          <div
            className={`flex h-7 w-7 items-center justify-center rounded-lg shrink-0 transition-colors ${
              isActive
                ? "bg-primary text-primary-foreground shadow-2xs"
                : "bg-muted text-muted-foreground group-hover:text-foreground"
            }`}
          >
            <Icon className="h-4 w-4" />
          </div>
        </div>

        {/* Label and Item Count */}
        <div className={`min-w-0 flex-1 flex items-center gap-2 transition-all duration-200 ${!isPinned ? 'opacity-0 w-0 overflow-hidden group-hover/sidebar:w-auto group-hover/sidebar:opacity-100' : ''}`}>
          <span
            className={`text-sm truncate select-none ${
              isActive
                ? "font-semibold text-foreground"
                : "text-foreground/90 font-medium"
            } ${!visible ? "line-through text-muted-foreground/70" : ""}`}
          >
            {label}
          </span>
          {itemCount !== null && itemCount !== undefined && itemCount > 0 && (
            <span className="inline-flex items-center justify-center px-1.5 py-0.5 text-xs font-mono font-medium rounded-full bg-muted text-muted-foreground border border-border/50">
              {itemCount}
            </span>
          )}
        </div>
      </button>

      {/* ── 3. Action Controls: Visibility Toggle + Overflow Menu ── */}
      <div className={`flex items-center gap-0.5 shrink-0 transition-all duration-200 ${!isPinned ? 'w-0 pr-0 opacity-0 overflow-hidden group-hover/sidebar:w-auto group-hover/sidebar:pr-1.5 group-hover/sidebar:opacity-100' : 'pr-1.5'}`}>
        {/* Visibility Toggle Button */}
        <button
          type="button"
          onClick={onToggleVisibility}
          aria-label={visible ? `Hide ${label} section` : `Show ${label} section`}
          title={visible ? "Hide section from site" : "Show section on site"}
          className={`h-9 w-9 flex items-center justify-center rounded-lg transition-colors cursor-pointer outline-none focus-visible:ring-2 focus-visible:ring-primary ${
            !visible
              ? "text-amber-600 dark:text-amber-400 bg-amber-500/10"
              : "text-muted-foreground/60 hover:text-foreground hover:bg-muted"
          }`}
        >
          {visible ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
        </button>

        {/* 3-Dot More Actions Menu Trigger */}
        <div className="relative">
          <button
            ref={menuTriggerRef}
            type="button"
            onClick={() => setIsMenuOpen((prev) => !prev)}
            aria-label={`More options for ${label} section`}
            aria-expanded={isMenuOpen}
            className={`h-9 w-9 flex items-center justify-center rounded-lg text-muted-foreground/60 hover:text-foreground hover:bg-muted transition-colors outline-none cursor-pointer focus-visible:ring-2 focus-visible:ring-primary ${
              isMenuOpen ? "bg-muted text-foreground" : ""
            }`}
          >
            <MoreVertical className="h-4 w-4" />
          </button>

          {/* Action Menu Dropdown */}
          {isMenuOpen && (
            <div
              ref={menuRef}
              role="menu"
              aria-label={`Actions for ${label} section`}
              className="absolute right-0 top-full mt-1 w-44 rounded-xl border border-border bg-card shadow-xl p-1 z-50 animate-in fade-in-50 zoom-in-95 duration-100"
            >
              {/* Move Up */}
              {onMoveUp && (
                <button
                  type="button"
                  role="menuitem"
                  disabled={isFirst || disabled}
                  onClick={() => {
                    setIsMenuOpen(false);
                    onMoveUp();
                  }}
                  className="flex items-center gap-2 w-full px-2.5 py-1.5 rounded-lg text-xs font-medium text-foreground hover:bg-muted transition-colors disabled:opacity-30 disabled:pointer-events-none cursor-pointer outline-none focus-visible:bg-muted"
                >
                  <ChevronUp className="h-3.5 w-3.5 text-muted-foreground" />
                  <span>Move Up</span>
                </button>
              )}

              {/* Move Down */}
              {onMoveDown && (
                <button
                  type="button"
                  role="menuitem"
                  disabled={isLast || disabled}
                  onClick={() => {
                    setIsMenuOpen(false);
                    onMoveDown();
                  }}
                  className="flex items-center gap-2 w-full px-2.5 py-1.5 rounded-lg text-xs font-medium text-foreground hover:bg-muted transition-colors disabled:opacity-30 disabled:pointer-events-none cursor-pointer outline-none focus-visible:bg-muted"
                >
                  <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" />
                  <span>Move Down</span>
                </button>
              )}

              {/* Duplicate Section */}
              {onDuplicate && (
                <button
                  type="button"
                  role="menuitem"
                  disabled={disabled}
                  onClick={() => {
                    setIsMenuOpen(false);
                    onDuplicate();
                  }}
                  className="flex items-center gap-2 w-full px-2.5 py-1.5 rounded-lg text-xs font-medium text-foreground hover:bg-muted transition-colors disabled:opacity-30 disabled:pointer-events-none cursor-pointer outline-none focus-visible:bg-muted"
                >
                  <Copy className="h-3.5 w-3.5 text-muted-foreground" />
                  <span>Duplicate</span>
                </button>
              )}

              {/* Toggle Visibility */}
              <button
                type="button"
                role="menuitem"
                onClick={() => {
                  setIsMenuOpen(false);
                  onToggleVisibility();
                }}
                className="flex items-center gap-2 w-full px-2.5 py-1.5 rounded-lg text-xs font-medium text-foreground hover:bg-muted transition-colors cursor-pointer outline-none focus-visible:bg-muted"
              >
                {visible ? (
                  <>
                    <EyeOff className="h-3.5 w-3.5 text-muted-foreground" />
                    <span>Hide from Site</span>
                  </>
                ) : (
                  <>
                    <Eye className="h-3.5 w-3.5 text-muted-foreground" />
                    <span>Show on Site</span>
                  </>
                )}
              </button>

              {/* Delete Section */}
              {onDelete && (
                <>
                  <div className="my-1 border-t border-border" />
                  <button
                    type="button"
                    role="menuitem"
                    onClick={() => {
                      setIsMenuOpen(false);
                      onDelete();
                    }}
                    className="flex items-center gap-2 w-full px-2.5 py-1.5 rounded-lg text-xs font-medium text-destructive hover:bg-destructive/10 transition-colors cursor-pointer outline-none focus-visible:bg-destructive/10"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                    <span>Delete Section</span>
                  </button>
                </>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/**
 * Presentational drag overlay preview (1.5° tilt, elevated shadow, pointer-events-none).
 */
export function SectionDragOverlayItem({
  label,
  icon: Icon,
  itemCount,
}: {
  label: string;
  icon: LucideIcon;
  itemCount?: number | null;
}) {
  return (
    <div className="flex items-center justify-between rounded-xl border border-primary/50 bg-card p-2.5 shadow-2xl ring-2 ring-primary/40 rotate-[1.5deg] scale-105 pointer-events-none">
      <div className="flex items-center gap-2.5 min-w-0">
        <div className="flex h-7 w-7 items-center justify-center rounded-lg bg-primary text-primary-foreground shadow-xs">
          <Icon className="h-4 w-4" />
        </div>
        <span className="text-sm font-semibold text-foreground truncate">{label}</span>
      </div>
      {itemCount !== null && itemCount !== undefined && itemCount > 0 && (
        <span className="inline-flex items-center justify-center px-2 py-0.5 text-xs font-mono font-medium rounded-full bg-muted text-muted-foreground border border-border">
          {itemCount}
        </span>
      )}
    </div>
  );
}
